import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { getBookingsList, confirmBooking, declineBooking } from '../../api/bookings';
import { getOfferings } from '../../api/offerings';
import { getQuoteRequests } from '../../api/quotes';
import './AdventureDashboardPage.css';

export default function AdventureDashboardPage() {
  const navigate = useNavigate();

  // View States
  const [viewMode, setViewMode] = useState('customer'); // customer or provider

  // Data States
  const [customerBookings, setCustomerBookings] = useState([]);
  const [providerBookings, setProviderBookings] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [customerQuotes, setCustomerQuotes] = useState([]);
  const [providerQuotes, setProviderQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const currentUserId = localStorage.getItem("userId") || "";

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [custBookings, provBookings, offeringsList, custQuotes, provQuotes] = await Promise.all([
        getBookingsList({ role: 'customer' }).catch(e => {
          console.warn("Failed to load customer bookings:", e);
          return [];
        }),
        getBookingsList({ role: 'provider' }).catch(e => {
          console.warn("Failed to load provider bookings:", e);
          return [];
        }),
        getOfferings({ providerId: currentUserId }).catch(e => {
          console.warn("Failed to load offerings:", e);
          return { data: [] };
        }),
        getQuoteRequests({ role: 'customer' }).catch(e => {
          console.warn("Failed to load customer quotes:", e);
          return [];
        }),
        getQuoteRequests({ role: 'provider' }).catch(e => {
          console.warn("Failed to load provider quotes:", e);
          return [];
        })
      ]);

      setCustomerBookings(custBookings);
      setProviderBookings(provBookings);
      setOfferings(offeringsList.data || offeringsList || []);
      setCustomerQuotes(custQuotes);
      setProviderQuotes(provQuotes);
    } catch (err) {
      console.error("Dashboard fetching error:", err);
      setError("Failed to load dashboard. Please verify your connection.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Inline Actions
  const handleConfirmRequest = async (bookingId) => {
    const notes = window.prompt("Enter any notes for the customer (optional):", "I will arrive on time. Looking forward to it!");
    if (notes === null) return;
    setIsLoading(true);
    try {
      await confirmBooking(bookingId, notes);
      fetchDashboardData();
    } catch (err) {
      console.error("Failed to confirm order:", err);
      alert("Failed to confirm order request. Please try again.");
      setIsLoading(false);
    }
  };

  const handleDeclineRequest = async (bookingId) => {
    const msg = window.prompt("Enter decline message to customer (optional):", "Unavailable at this scheduled slot.");
    if (msg === null) return;
    setIsLoading(true);
    try {
      await declineBooking(bookingId, "unavailable", msg);
      fetchDashboardData();
    } catch (err) {
      console.error("Failed to decline order:", err);
      alert("Failed to decline order request. Please try again.");
      setIsLoading(false);
    }
  };

  const formatPriceVal = (amount, model, currency) => {
    if (model === 'free') return 'Free';
    if (model === 'cod') return 'Cash on Delivery';

    let cleanCurrency = (currency || 'INR').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'INR';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cleanCurrency
      }).format(amount || 0);
    } catch (e) {
      return `${cleanCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  // Calculations — Customer Mode
  const activeCustomerBookings = customerBookings.filter(
    b => b.status === 'pending' || b.status === 'confirmed' || b.status === 'in_progress'
  );
  const completedCustomerBookings = customerBookings.filter(
    b => b.status === 'completed' || b.status === 'closed'
  );
  const totalSpent = completedCustomerBookings.reduce((sum, b) => sum + (b.calculatedTotal || 0), 0);

  // Calculations — Provider Mode
  const pendingRequests = providerBookings.filter(b => b.status === 'pending');
  const activeJobs = providerBookings.filter(b => b.status === 'confirmed' || b.status === 'in_progress');
  const completedProviderBookings = providerBookings.filter(
    b => b.status === 'completed' || b.status === 'closed'
  );
  const totalEarnings = completedProviderBookings.reduce((sum, b) => sum + (b.calculatedTotal || 0), 0);

  return (
    <div className="AdventureDashboardPage">
      <div className="adventureDashboardContainer">

        {/* Main Amalgamate Header */}
        <div className="headerContain">
          <Header />
        </div>

        <div className="adventureDashboardContainer2">

          {/* Main Amalgamate Sidebar */}
          <div className="sidebarContain">
            <Sidebar />
          </div>

          {/* Main Content */}
          <main className="adv-db-main">

            {/* Page Header */}
            <div className="adv-db-page-header">
              <h1 className="adv-db-welcome">
                Marketplace <span className="adv-db-highlight">Dashboard</span>
              </h1>
              <p className="adv-db-sub">Manage your service offerings, active orders, and incoming requests.</p>
            </div>

            {/* View Mode Switcher */}
            <div className="adv-db-switcher">
              <button
                className={`adv-db-switch-btn ${viewMode === 'customer' ? 'adv-db-switch-btn--active' : ''}`}
                onClick={() => setViewMode('customer')}
              >
                🧑 My Purchases
              </button>
              <button
                className={`adv-db-switch-btn ${viewMode === 'provider' ? 'adv-db-switch-btn--active' : ''}`}
                onClick={() => setViewMode('provider')}
              >
                💼 My Sales / Requests
              </button>
            </div>

            {isLoading ? (
              <div className="adv-db-loading">Loading dashboard statistics...</div>
            ) : error ? (
              <div className="adv-db-error">{error}</div>
            ) : (
              <>
                {/* ── CLIENT DASHBOARD ── */}
                {viewMode === 'customer' && (
                  <>
                    {/* Stats */}
                    <div className="adv-db-stats-grid adv-db-stats-grid--4">
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">💳</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Total Spent</span>
                          <strong className="adv-db-stat-value">
                            {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'INR' }).format(totalSpent)}
                          </strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">📅</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Active Orders</span>
                          <strong className="adv-db-stat-value">{activeCustomerBookings.length} Active</strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">🎫</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Total Orders</span>
                          <strong className="adv-db-stat-value">{customerBookings.length} Ordered</strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/facilitate/quotes')}>
                        <div className="adv-db-stat-icon">💬</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Custom Quotes</span>
                          <strong className="adv-db-stat-value">
                            {customerQuotes.filter(q => q.status === 'requested' || q.status === 'quoted').length} Active
                          </strong>
                        </div>
                      </div>
                    </div>

                    {/* Upcoming Bookings */}
                    <div className="adv-db-section">
                      <h2 className="adv-db-section-title">Upcoming Service Schedule</h2>
                      <div className="adv-db-list">
                        {activeCustomerBookings.length === 0 ? (
                          <div className="adv-db-empty">
                            <p>You don't have any upcoming orders scheduled.</p>
                            <button
                              className="adv-db-empty-btn"
                              onClick={() => navigate('/facilitate')}
                            >
                              Find Offerings
                            </button>
                          </div>
                        ) : (
                          activeCustomerBookings.map((booking) => (
                            <div key={booking.bookingId} className="adv-db-booking-card">
                              <div className="adv-db-booking-card__left">
                                <img
                                  src={booking.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=120&q=80"}
                                  alt={booking.offeringTitle}
                                  className="adv-db-booking-card__img"
                                />
                                <div>
                                  <h3 className="adv-db-booking-card__name">{booking.offeringTitle}</h3>
                                  <div className="adv-db-booking-card__meta">
                                    <span>📅 {booking.date} at {booking.time}</span>
                                    <span>🧑 Provider: {booking.providerName}</span>
                                    <span className={`adv-db-status adv-db-status--${booking.status}`}>
                                      {booking.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/facilitate/orders/${booking.bookingId}`)}
                                className="adv-db-outline-btn"
                              >
                                View Details
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Active Custom Quote Inquiries */}
                    <div className="adv-db-section" style={{ marginTop: '28px' }}>
                      <div className="adv-db-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 className="adv-db-section-title" style={{ margin: 0 }}>Active Custom Quote Inquiries</h2>
                        <button
                          className="adv-db-link-btn"
                          onClick={() => navigate('/facilitate/quotes')}
                        >
                          View All Quotes →
                        </button>
                      </div>
                      <div className="adv-db-list" style={{ marginTop: '14px' }}>
                        {customerQuotes.filter(q => q.status === 'requested' || q.status === 'quoted').length === 0 ? (
                          <div className="adv-db-empty">
                            <p>You don't have any active custom quote inquiries.</p>
                            <button
                              className="adv-db-empty-btn"
                              onClick={() => navigate('/facilitate/offerings')}
                            >
                              Request Custom Quote
                            </button>
                          </div>
                        ) : (
                          customerQuotes.filter(q => q.status === 'requested' || q.status === 'quoted').map((quote) => (
                            <div key={quote.id} className="adv-db-booking-card">
                              <div className="adv-db-booking-card__left">
                                <img
                                  src={quote.providerAvatarUrl || "/profilePic.jpg"}
                                  alt={quote.providerName}
                                  className="adv-db-booking-card__avatar"
                                />
                                <div>
                                  <h3 className="adv-db-booking-card__name">{quote.providerName}</h3>
                                  <div className="adv-db-booking-card__meta">
                                    <span>Requested on {new Date(quote.createdAt).toLocaleDateString()}</span>
                                    <span>Budget Hint: {quote.budgetHint > 0 ? formatPriceVal(quote.budgetHint, 'flat', quote.currency) : "Flexible"}</span>
                                    <span className={`adv-db-status adv-db-status--${quote.status}`}>
                                      {quote.status === 'requested' ? 'pending review' : quote.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/facilitate/quotes/${quote.id}`)}
                                className="adv-db-outline-btn"
                              >
                                View Inquiry
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* ── PROVIDER DASHBOARD ── */}
                {viewMode === 'provider' && (
                  <>
                    {/* Stats */}
                    <div className="adv-db-stats-grid adv-db-stats-grid--5">
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">💰</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Total Earnings</span>
                          <strong className="adv-db-stat-value">
                            {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'INR' }).format(totalEarnings)}
                          </strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">📥</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Pending Requests</span>
                          <strong className="adv-db-stat-value">{pendingRequests.length} Requests</strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/facilitate/quotes/inbox')}>
                        <div className="adv-db-stat-icon">💬</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Quote Inquiries</span>
                          <strong className="adv-db-stat-value">
                            {providerQuotes.filter(q => q.status === 'requested').length} New
                          </strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">🛠️</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Active Jobs</span>
                          <strong className="adv-db-stat-value">{activeJobs.length} Ongoing</strong>
                        </div>
                      </div>
                      <div className="adv-db-stat-card">
                        <div className="adv-db-stat-icon">🛍️</div>
                        <div className="adv-db-stat-info">
                          <span className="adv-db-stat-title">Your Offerings</span>
                          <strong className="adv-db-stat-value">{offerings.length} Active</strong>
                        </div>
                      </div>
                    </div>

                    {/* Pending Requests */}
                    <div className="adv-db-section">
                      <div className="adv-db-section-header">
                        <h2 className="adv-db-section-title">Incoming Pending Requests</h2>
                        <button
                          className="adv-db-link-btn"
                          onClick={() => navigate('/facilitate/orders/inbox')}
                        >
                          View Inbox →
                        </button>
                      </div>
                      <div className="adv-db-list">
                        {pendingRequests.length === 0 ? (
                          <div className="adv-db-empty">
                            <p>No new pending requests.</p>
                          </div>
                        ) : (
                          pendingRequests.slice(0, 3).map((request) => (
                            <div key={request.bookingId} className="adv-db-booking-card">
                              <div className="adv-db-booking-card__left">
                                <img
                                  src={request.customerAvatarUrl || "/profilePic.jpg"}
                                  alt={request.customerName}
                                  className="adv-db-booking-card__avatar"
                                />
                                <div>
                                  <h4 className="adv-db-booking-card__name">{request.customerName}</h4>
                                  <p className="adv-db-booking-card__meta-text">
                                    Requested: <strong>{request.offeringTitle}</strong> on {request.date} at {request.time}
                                  </p>
                                </div>
                              </div>
                              <div className="adv-db-booking-card__actions">
                                <span className="adv-db-price">
                                  {formatPriceVal(request.price, request.pricingModel, request.currency)}
                                </span>
                                <button
                                  onClick={() => handleDeclineRequest(request.bookingId)}
                                  className="adv-db-decline-btn"
                                >
                                  Decline
                                </button>
                                <button
                                  onClick={() => handleConfirmRequest(request.bookingId)}
                                  className="adv-db-confirm-btn"
                                >
                                  Confirm
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Incoming Quote Inquiries */}
                    <div className="adv-db-section" style={{ marginTop: '28px' }}>
                      <div className="adv-db-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 className="adv-db-section-title" style={{ margin: 0 }}>Incoming Quote Inquiries</h2>
                        <button
                          className="adv-db-link-btn"
                          onClick={() => navigate('/facilitate/quotes/inbox')}
                        >
                          View Quote Inbox →
                        </button>
                      </div>
                      <div className="adv-db-list" style={{ marginTop: '14px' }}>
                        {providerQuotes.filter(q => q.status === 'requested').length === 0 ? (
                          <div className="adv-db-empty">
                            <p>No new quote inquiries.</p>
                          </div>
                        ) : (
                          providerQuotes.filter(q => q.status === 'requested').slice(0, 3).map((inquiry) => (
                            <div key={inquiry.id} className="adv-db-booking-card">
                              <div className="adv-db-booking-card__left">
                                <img
                                  src={inquiry.customerAvatarUrl || "/profilePic.jpg"}
                                  alt={inquiry.customerName}
                                  className="adv-db-booking-card__avatar"
                                />
                                <div>
                                  <h4 className="adv-db-booking-card__name">{inquiry.customerName}</h4>
                                  <p className="adv-db-booking-card__meta-text">
                                    Requested custom quote on {new Date(inquiry.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="adv-db-booking-card__actions">
                                <span className="adv-db-price">
                                  Budget: {inquiry.budgetHint > 0 ? formatPriceVal(inquiry.budgetHint, 'flat', inquiry.currency) : "Flexible"}
                                </span>
                                <button
                                  onClick={() => navigate(`/facilitate/quotes/${inquiry.id}`)}
                                  className="adv-db-confirm-btn"
                                >
                                  View & Reply
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Active Jobs */}
                    <div className="adv-db-section">
                      <h2 className="adv-db-section-title">Active Service Jobs</h2>
                      <div className="adv-db-list">
                        {activeJobs.length === 0 ? (
                          <div className="adv-db-empty">
                            <p>No active service deliveries.</p>
                          </div>
                        ) : (
                          activeJobs.map((job) => (
                            <div key={job.bookingId} className="adv-db-booking-card">
                              <div>
                                <h3 className="adv-db-booking-card__name">{job.offeringTitle}</h3>
                                <div className="adv-db-booking-card__meta">
                                  <span>Client: <strong>{job.customerName}</strong></span>
                                  <span>📅 {job.date} at {job.time}</span>
                                  <span className={`adv-db-status adv-db-status--${job.status}`}>
                                    {job.status}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => navigate(`/facilitate/orders/${job.bookingId}`)}
                                className="adv-db-outline-btn"
                              >
                                Manage Delivery
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
